/*
 * Admin Trends grid. Traceability: FR-004 references
 * .verified/features/apartment-filters-everywhere/spec.md — the shared
 * ApartmentFilterBar predicates layered onto this page's existing controls.
 */
import React, { useCallback, useEffect, useState } from "react";
import { ApartmentStatTrend, Convert, OwnerAirbnbReview } from "@/models/responses";
import { ColDef, GridApi } from "ag-grid-community";
import { euroFormatterTwoDecimals, occupancyBarRenderer, paramsEuroFormatter, percentageFormatterMultipliedByHundred } from "@/utils/formatters";
import Grid, { GridDropdownFilter } from "@/lib/organisms/Grid";
import { PeriodName } from "@/models";
import { useAppData } from "@/hooks/useAppData";
import { useUserSettingsDispatch } from "@/store/slices/userSettingsDispatch";
import { useActiveApartmentEntities } from "@/store/slices/apartments";
import { fetchOptions } from "@/hooks/fetchOptions";
import { apiUrl } from "@/layouts/apiUrl";
import { formatDateToISODate } from "@/utils/dates";
import { PeriodDates, usePeriodDates } from "@/hooks/usePeriodDates";
import { useApartmentFilters } from "@/hooks/useApartmentFilters";
import ApartmentFilterBar from "@/lib/components/apartment-filter-bar";

interface TrendsRow {
    apartmentId: number;
    numberOfVisits: number;
    grossIncome: number;
    grossIncomePreviousPeriod: number;
    grossIncomePreviousYear: number;
    netIncome: number;
    netIncomePreviousPeriod: number;
    netIncomePreviousYear: number;
    netAdr: number;
    netAdrPreviousPeriod: number;
    netAdrPreviousYear: number;
    grossAdr: number;
    grossAdrPreviousPeriod: number;
    grossAdrPreviousYear: number;
    occupancy: number;
    occupancyPreviousPeriod: number;
    occupancyPreviousYear: number;
    daysBooked: number;
    daysBookedPreviousPeriod: number;
    daysBookedPreviousYear: number;
    daysAvailable: number;
    daysAvailablePreviousPeriod: number;
    daysAvailablePreviousYear: number;
    payout: number;
    payoutPreviousYear: number;
    payoutRatio: number;
    payoutRatioPreviousYear: number;
    reviewScore: number | null;
    reviewScorePreviousPeriod: number | null;
    reviewScorePreviousYear: number | null;
}

const AdminTrendsPage: React.FC = () => {
    const [apartmentTrends, setApartmentTrends] = useState<TrendsRow[]>([]);
    const [gridApi, setGridApi] = useState<GridApi | null>(null);

    const { yearOptions, periodOptions, selectedPeriod, selectedYear, groupOptions, selectedGroup } = useAppData();

    const apartmentEntities = useActiveApartmentEntities();

    const periodDates = usePeriodDates(selectedYear, selectedPeriod);

    const { setSelectedYear, setSelectedPeriod, setSelectedGroup } = useUserSettingsDispatch();

    // Shared apartment-filter predicates layered on when painting rows below;
    // the existing Period / Year / Group controls stay untouched (FR-004).
    const { predicates } = useApartmentFilters();

    const mapTrendToRow = useCallback(
        (
            trend: ApartmentStatTrend,
            reviewsCurrentPeriod: OwnerAirbnbReview | undefined,
            reviewsPreviousPeriod: OwnerAirbnbReview | undefined,
            reviewsPreviousYear: OwnerAirbnbReview | undefined,
        ) => {
            return {
                apartmentId: trend.apartmentId,
                numberOfVisits: trend.numberOfVisits,
                grossIncome: trend.grossIncome,
                grossIncomePreviousPeriod: trend.grossIncomePreviousPeriod,
                grossIncomePreviousYear: trend.grossIncomePreviousYear,
                netIncome: trend.netIncome,
                netIncomePreviousPeriod: trend.netIncomePreviousPeriod,
                netIncomePreviousYear: trend.netIncomePreviousYear,
                netAdr: trend.netAdr,
                netAdrPreviousPeriod: trend.netAdrPreviousPeriod,
                netAdrPreviousYear: trend.netAdrPreviousYear,
                grossAdr: trend.grossAdr,
                grossAdrPreviousPeriod: trend.grossAdrPreviousPeriod,
                grossAdrPreviousYear: trend.grossAdrPreviousYear,
                occupancy: trend.occupancy,
                occupancyPreviousPeriod: trend.occupancyPreviousPeriod,
                occupancyPreviousYear: trend.occupancyPreviousYear,
                daysBooked: trend.daysBooked,
                daysBookedPreviousPeriod: trend.daysBookedPreviousPeriod,
                daysBookedPreviousYear: trend.daysBookedPreviousYear,
                daysAvailable: trend.daysAvailable,
                daysAvailablePreviousPeriod: trend.daysAvailablePreviousPeriod,
                daysAvailablePreviousYear: trend.daysAvailablePreviousYear,
                payout: trend.payout,
                payoutPreviousYear: trend.payoutPreviousYear,
                payoutRatio: trend.payoutRatio,
                payoutRatioPreviousYear: trend.payoutRatioPreviousYear,
                reviewScore: reviewsCurrentPeriod?.overallScore ?? null,
                reviewScorePreviousPeriod: reviewsPreviousPeriod?.overallScore ?? null,
                reviewScorePreviousYear: reviewsPreviousYear?.overallScore ?? null,
            };
        },
        [],
    );

    const getReviewsForPeriod = useCallback(async (periodEndDate: Date) => {
        const firstDayOfNextMonth = new Date(periodEndDate.getFullYear(), periodEndDate.getMonth() + 1, 1);
        const reviewsUrl = `${apiUrl}/api/admin/airbnb-reviews?date=${formatDateToISODate(firstDayOfNextMonth)}`;
        const response = await fetch(reviewsUrl, fetchOptions);
        if (!response.ok) {
            throw new Error("Network response was not ok");
        }
        const data = await response.text();
        return Convert.toGetOwnerAirbnbReviewsResponse(data).reviews;
    }, []);

    useEffect(() => {
        if (!gridApi) {
            return;
        }
        const filteredRows = apartmentTrends.filter((trend) => {
            if (selectedGroup !== 0 && selectedGroup !== apartmentEntities[trend.apartmentId]?.groupId) {
                return false;
            }
            // A trend for an unknown apartment (entities map miss) is excluded:
            // predicates can't run against `undefined` and an orphan row is wrong.
            const apartment = apartmentEntities[trend.apartmentId];
            if (!apartment) {
                return false;
            }
            return predicates.every((predicate) => predicate(apartment));
        });
        gridApi.setGridOption("rowData", filteredRows);
    }, [apartmentEntities, apartmentTrends, gridApi, selectedGroup, predicates]);

    const getApartmentTrends = useCallback(
        async (periodDates: PeriodDates, year: string, period: string): Promise<void> => {
            // Three independent review fetches (distinct dates) issued in
            // parallel to halve page-load latency.
            const [reviewsCurrentPeriod, reviewsPreviousPeriod, reviewsPreviousYear] = await Promise.all([
                getReviewsForPeriod(periodDates.current.endDate),
                getReviewsForPeriod(periodDates.previousPeriod.endDate),
                getReviewsForPeriod(periodDates.previousYear.endDate),
            ]);
            const url = `${apiUrl}/api/admin/apartments/trend-stats?year=${year}&period=${period}`;
            fetch(url, fetchOptions)
                .then((response) => {
                    if (response) {
                        return response.text();
                    }
                    throw new Error("Unauthorized");
                })
                .then((response) => {
                    let getApartmentStatTrends = Convert.toGetApartmentStatTrends(response);
                    let gridRows = getApartmentStatTrends.apartmentStatTrends
                        .map((trend) => {
                            const reviewsForApartmentCurrentPeriod = reviewsCurrentPeriod.find((review) => review.apartmentId === trend.apartmentId);
                            const reviewsForApartmentPreviousPeriod = reviewsPreviousPeriod.find((review) => review.apartmentId === trend.apartmentId);
                            const reviewsForApartmentPreviousYear = reviewsPreviousYear.find((review) => review.apartmentId === trend.apartmentId);
                            return mapTrendToRow(trend, reviewsForApartmentCurrentPeriod, reviewsForApartmentPreviousPeriod, reviewsForApartmentPreviousYear);
                        })
                        .sort((a, b) => {
                            const apartmentA = apartmentEntities[a.apartmentId] || {
                                name: "Unknown",
                            };
                            const apartmentB = apartmentEntities[b.apartmentId] || {
                                name: "Unknown",
                            };
                            return apartmentA.name.localeCompare(apartmentB.name);
                        });
                    setApartmentTrends(gridRows);
                })
                .catch((error) => console.log(error));
        },
        [apartmentEntities, getReviewsForPeriod, mapTrendToRow],
    );

    const daysBookedColumn: ColDef<TrendsRow> = {
        field: "daysBooked",
        headerName: "Days Booked",
        width: 100,
        cellDataType: "number",
        filter: "agNumberColumnFilter",
    };
    const grossIncomeColumn: ColDef<TrendsRow> = {
        field: "grossIncome",
        headerName: "Gross Income",
        valueFormatter: paramsEuroFormatter,
        cellClass: "currencyFormat",
        cellDataType: "number",
        filter: "agNumberColumnFilter",
        width: 110,
        filterValueGetter: (value) => {
            if (value.data !== undefined) {
                return Math.round(value.data.grossIncome);
            }
        },
    };
    const netIncomeColumn: ColDef<TrendsRow> = {
        field: "netIncome",
        headerName: "Net Income",
        valueFormatter: paramsEuroFormatter,
        cellClass: "currencyFormat",
        cellDataType: "number",
        filter: "agNumberColumnFilter",
        width: 110,
        filterValueGetter: (value) => {
            if (value.data !== undefined) {
                return Math.round(value.data.netIncome);
            }
        },
    };
    const numberOfVisitsColumn: ColDef<TrendsRow> = {
        field: "numberOfVisits",
        headerName: "Number of Visits",
        width: 130,
        cellDataType: "number",
        filter: "agNumberColumnFilter",
    };
    const netAdrColumn: ColDef<TrendsRow> = {
        field: "netAdr",
        headerName: "Net Adr",
        valueFormatter: paramsEuroFormatter,
        cellClass: "currencyFormat",
        cellDataType: "number",
        filter: "agNumberColumnFilter",
        width: 95,
        filterValueGetter: (value) => {
            if (value.data !== undefined) {
                return Math.round(value.data.netAdr);
            }
        },
    };
    const grossAdrColumn: ColDef<TrendsRow> = {
        field: "grossAdr",
        headerName: "Gross Adr",
        valueFormatter: paramsEuroFormatter,
        cellClass: "currencyFormat",
        cellDataType: "number",
        filter: "agNumberColumnFilter",
        width: 95,
        filterValueGetter: (value) => {
            if (value.data !== undefined) {
                return Math.round(value.data.grossAdr);
            }
        },
    };
    let reviewScoreCurrent: ColDef<TrendsRow, number | null> = {
        field: "reviewScore",
        headerName: "Airbnb Review Rating",
        valueFormatter: (val) => (val.value === null ? "-" : (val.value?.toFixed(2) ?? "-")),
        cellDataType: "number",
        cellClass: "booleanType",
        filter: "agNumberColumnFilter",
        width: 150,
    };
    let reviewScorePreviousPeriod: ColDef<TrendsRow, number | null> = {
        field: "reviewScorePreviousPeriod",
        headerName: "Airbnb Review Rating Previous Period",
        valueFormatter: (val) => (val.value === null ? "-" : (val.value?.toFixed(2) ?? "-")),
        cellDataType: "number",
        cellClass: "booleanType",
        filter: "agNumberColumnFilter",
        width: 150,
    };
    let reviewScorePreviousYear: ColDef<TrendsRow, number | null> = {
        field: "reviewScorePreviousYear",
        headerName: "Airbnb Review Rating Previous Year",
        valueFormatter: (val) => (val.value === null ? "-" : (val.value?.toFixed(2) ?? "-")),
        cellDataType: "number",
        cellClass: "booleanType",
        filter: "agNumberColumnFilter",
        width: 150,
    };
    const columnDefinitions: ColDef[] = [
        {
            field: "apartmentId",
            headerName: "Apartment",
            valueGetter: (params) => {
                if (params.data) {
                    return apartmentEntities[params.data.apartmentId]?.name;
                }
            },
            width: 280,
            cellDataType: "text",
            filter: "agTextColumnFilter",
        },
        numberOfVisitsColumn,
        {
            field: "occupancy",
            headerName: "Occupancy",
            cellRenderer: occupancyBarRenderer,
            cellClass: "percentFormatNoMultiplier",
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            filterValueGetter: (value) => {
                return Math.round(value.data.occupancy);
            },
            width: 140,
        },
        {
            field: "occupancyPreviousPeriod",
            headerName: "Occupancy Previous Period",
            cellRenderer: occupancyBarRenderer,
            cellClass: "percentFormatNoMultiplier",
            filter: "agNumberColumnFilter",
            filterValueGetter: (value) => {
                return Math.round(value.data.occupancyPreviousPeriod);
            },
            width: 140,
        },
        {
            field: "occupancyPreviousYear",
            headerName: "Occupancy Previous Year",
            cellRenderer: occupancyBarRenderer,
            cellClass: "percentFormatNoMultiplier",
            filter: "agNumberColumnFilter",
            filterValueGetter: (value) => {
                return Math.round(value.data.occupancyPreviousYear);
            },
            width: 140,
        },
        daysBookedColumn,
        {
            field: "daysBookedPreviousPeriod",
            headerName: "Days Booked Previous Period",
            width: 150,
            cellDataType: "number",
            filter: "agNumberColumnFilter",
        },
        {
            field: "daysBookedPreviousYear",
            headerName: "Days Booked Previous Year",
            width: 150,
            cellDataType: "number",
            filter: "agNumberColumnFilter",
        },
        {
            field: "daysAvailable",
            headerName: "Days Available",
            width: 100,
            cellDataType: "number",
            filter: "agNumberColumnFilter",
        },
        {
            field: "daysAvailablePreviousPeriod",
            headerName: "Days Available Previous Period",
            width: 150,
            cellDataType: "number",
            filter: "agNumberColumnFilter",
        },
        {
            field: "daysAvailablePreviousYear",
            headerName: "Days Available Previous Year",
            width: 150,
            cellDataType: "number",
            filter: "agNumberColumnFilter",
        },
        grossIncomeColumn,
        {
            field: "grossIncomePreviousPeriod",
            headerName: "Gross Income Previous Period",
            valueFormatter: paramsEuroFormatter,
            cellClass: "currencyFormat",
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 140,
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.grossIncomePreviousPeriod);
                }
            },
        },
        {
            field: "grossIncomePreviousYear",
            headerName: "Gross Income Previous Year",
            valueFormatter: paramsEuroFormatter,
            cellClass: "currencyFormat",
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 140,
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.grossIncomePreviousYear);
                }
            },
        },
        netIncomeColumn,
        {
            field: "netIncomePreviousPeriod",
            headerName: "Net Income Previous Period",
            valueFormatter: paramsEuroFormatter,
            cellClass: "currencyFormat",
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 110,
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.netIncomePreviousPeriod);
                }
            },
        },
        {
            field: "netIncomePreviousYear",
            headerName: "Net Income Previous Year",
            valueFormatter: paramsEuroFormatter,
            cellClass: "currencyFormat",
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 110,
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.netIncomePreviousYear);
                }
            },
        },
        netAdrColumn,
        {
            field: "netAdrPreviousPeriod",
            headerName: "Net Adr Previous Period",
            valueFormatter: paramsEuroFormatter,
            cellClass: "currencyFormat",
            cellDataType: "number",
            width: 95,
            filter: "agNumberColumnFilter",
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.netAdrPreviousPeriod);
                }
            },
        },
        {
            field: "netAdrPreviousYear",
            headerName: "Net Adr Previous Year",
            valueFormatter: paramsEuroFormatter,
            cellClass: "currencyFormat",
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 95,
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.netAdrPreviousYear);
                }
            },
        },
        grossAdrColumn,
        {
            field: "grossAdrPreviousPeriod",
            headerName: "Gross Adr Previous Period",
            valueFormatter: paramsEuroFormatter,
            cellClass: "currencyFormat",
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 95,
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.grossAdrPreviousPeriod);
                }
            },
        },
        {
            field: "grossAdrPreviousYear",
            headerName: "Gross Adr Previous Year",
            valueFormatter: paramsEuroFormatter,
            cellClass: "currencyFormat",
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 95,
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.grossAdrPreviousYear);
                }
            },
        },
        {
            field: "payout",
            headerName: "Payout",
            valueFormatter: paramsEuroFormatter,
            cellClass: "currencyFormat",
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 110,
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.payout);
                }
            },
        },
        {
            field: "payoutPreviousYear",
            headerName: "Payout Previous Year",
            valueFormatter: paramsEuroFormatter,
            cellClass: "currencyFormat",
            cellDataType: "number",
            filter: "agNumberColumnFilter",
            width: 110,
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.payoutPreviousYear);
                }
            },
        },
        {
            field: "payoutRatio",
            headerName: "Payout Ratio",
            valueFormatter: percentageFormatterMultipliedByHundred,
            cellDataType: "number",
            cellClass: "percentFormat",
            filter: "agNumberColumnFilter",
            width: 110,
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.payoutRatio * 100);
                }
            },
        },
        {
            field: "payoutRatioPreviousYear",
            headerName: "Payout Ratio Previous Year",
            valueFormatter: percentageFormatterMultipliedByHundred,
            cellDataType: "number",
            cellClass: "percentFormat",
            filter: "agNumberColumnFilter",
            width: 110,
            filterValueGetter: (value) => {
                if (value.data !== undefined) {
                    return Math.round(value.data.payoutRatioPreviousYear * 100);
                }
            },
        },
        reviewScoreCurrent,
        reviewScorePreviousPeriod,
        reviewScorePreviousYear,
    ];

    useEffect(() => {
        void getApartmentTrends(periodDates, selectedYear.toString(), selectedPeriod);
    }, [selectedPeriod, selectedYear, apartmentEntities, yearOptions, getApartmentTrends, periodDates]);

    const gridDropdownFilters: GridDropdownFilter[] = [
        {
            label: "Period",
            options: periodOptions,
            selectedOption: selectedPeriod,
            onSelectionChanged: (period) => setSelectedPeriod(period as PeriodName),
        },
        {
            label: "Year",
            options: yearOptions,
            selectedOption: selectedYear.toString(),
            onSelectionChanged: (year) => setSelectedYear(Number(year)),
        },
        {
            label: "Group",
            options: groupOptions,
            selectedOption: selectedGroup.toString(),
            onSelectionChanged: (apartment) => setSelectedGroup(Number(apartment)),
        },
    ];

    return (
        <div className="flex flex-col space-y-2" data-testid="admin-trends-page">
            <ApartmentFilterBar />
            <Grid<TrendsRow>
                columnDefs={columnDefinitions}
                dropdownFilters={gridDropdownFilters}
                gridName={"Trends"}
                onGridReady={setGridApi}
                getRowId={(params) => params.data.apartmentId.toString()}
                initialRowData={apartmentTrends}
                columnsToAggregate={[
                    { colDef: daysBookedColumn, formatter: (x) => x },
                    { colDef: numberOfVisitsColumn, formatter: (x) => x },
                    { colDef: grossIncomeColumn, formatter: euroFormatterTwoDecimals },
                    { colDef: netIncomeColumn, formatter: euroFormatterTwoDecimals },
                    {
                        colDef: netAdrColumn,
                        formatter: euroFormatterTwoDecimals,
                        aggregateFunction: "avg",
                    },
                    {
                        colDef: grossAdrColumn,
                        formatter: euroFormatterTwoDecimals,
                        aggregateFunction: "avg",
                    },
                ]}
            />
        </div>
    );
};

export default AdminTrendsPage;
